import { Button } from '@design/components/button/Button';
import { TooltipTrigger } from '@design/components/tooltip/Tooltip';
import { toMentionPlainText } from '@terminal/components/chat/chat-input/mentions/markers';
import { TooltipContainer } from '@terminal/components/tooltip-container/TooltipContainer';
import { useTouchDevice } from '@terminal/hooks/MediaQueryHook';
import { useScheduleDrawerUrlSync } from '@terminal/hooks/use-schedule-drawer-url-sync/useScheduleDrawerUrlSync';
import { useListScheduledQueries } from '@terminal/react-query-hooks/alfred/useScheduledQueries';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { isAtActiveScheduleCap } from '@terminal/views/alfred/research/prompt-form/utils/scheduleCap';
import { useChat } from '@terminal/views/alfred/research/use-chat/useChat';
import { useUserUpload } from '@terminal/views/alfred/research/use-user-upload/useUserUpload';
import { useTranslation } from 'react-i18next';
import { ChatInputConfig } from '../../chat-input';
import styles from './ChatActionButtons.module.scss';

export interface ChatActionButtonsProps {
  isMessagePending: boolean;
  inputValue: string;
  abortRequest?: () => void;
  config?: ChatInputConfig;
  showRefineButton?: boolean;
  onRefineClick?: () => void;
}

export const ChatActionButtons = ({
  isMessagePending,
  inputValue,
  abortRequest,
  config,
  showRefineButton = false,
  onRefineClick,
}: ChatActionButtonsProps) => {
  const { t } = useTranslation('chat');
  const isTouchDevice = useTouchDevice();
  const isAlfredEverywhere = config?.alfredEverywhere;
  const { open: openScheduleDrawer } = useScheduleDrawerUrlSync();

  const analysisMode = useChat(state => state.analysisMode);

  const isScheduledTaskEnabled = useFlags(state =>
    state.getFeatureFlagState('scheduled_task_feature')
  );
  const isAlfredAskAnalystEnabled = useFlags(state =>
    state.getFeatureFlagState('alfred_ask_analyst')
  );
  const mentionsEnabled = useFlags(state =>
    state.getFeatureFlagState('REASON-2103-alfred-mentions')
  );

  const { data: scheduledQueriesData, isLoading: isScheduledQueriesLoading } =
    useListScheduledQueries({ status: 'active' }, isScheduledTaskEnabled);
  // Treat an unknown count (still loading) as at the limit to avoid a first-load
  // window where the button is enabled for users already at the cap.
  const isAtMaxScheduledTasks =
    isScheduledTaskEnabled &&
    (isScheduledQueriesLoading ||
      isAtActiveScheduleCap(scheduledQueriesData?.total_count ?? 0));

  const { uploadingFiles, hasUploadedFiles } = useUserUpload(state => ({
    uploadingFiles: state.files.filter(file => file.status === 'uploading'),
    hasUploadedFiles: state.files.length > 0,
  }));

  const hasEmptyInput = !inputValue.trim();
  const hasUploadingFiles = uploadingFiles.length > 0;
  const isSubmitButtonDisabled =
    hasEmptyInput ||
    hasUploadingFiles ||
    (isAlfredEverywhere && isMessagePending);

  const isScheduleButtonDisabled =
    hasEmptyInput || hasUploadedFiles || isAtMaxScheduledTasks;

  const handleScheduleClick = () => {
    openScheduleDrawer(toMentionPlainText(inputValue, mentionsEnabled));
  };

  const getScheduleTooltipLabel = () => {
    if (hasUploadedFiles) return t('chat:scheduleTaskDisabledWithFile');
    if (isAtMaxScheduledTasks)
      return t('chat:historySidebar.scheduleTaskDisabledMaxReached');
    if (hasEmptyInput) return t('chat:scheduleTaskDisabledNoInput');
    return t('chat:historySidebar.scheduleTask');
  };

  const refineButton = showRefineButton && !isTouchDevice && (
    <Button
      className={styles.refineButton}
      type="button"
      variant="empty"
      size="medium"
      iconName="Alfred"
      label={t('chat:promptRefinement.button')}
      onClick={onRefineClick}
      disabled={isSubmitButtonDisabled}
      data-testid="prompt-refinement-button"
    />
  );

  const submitButton = (
    <TooltipContainer
      label={
        isMessagePending
          ? t('chat:stopResponse')
          : isAlfredAskAnalystEnabled
            ? t('chat:analysisMode.sendToAnalyst')
            : null
      }
      inPortal
      trigger={[TooltipTrigger.Hover]}
      disabled={analysisMode === 'Auto'}
    >
      {isMessagePending && !isAlfredEverywhere ? (
        <Button
          className={styles.submitButton}
          type="button"
          data-testid="abort-button"
          iconName="Stop"
          onClick={abortRequest}
        />
      ) : (
        <Button
          className={styles.submitButton}
          type="submit"
          data-testid="submit-button"
          variant="primary"
          iconName="ArrowUp"
          disabled={isSubmitButtonDisabled}
        />
      )}
    </TooltipContainer>
  );

  if (!isScheduledTaskEnabled) {
    return (
      <div className={styles.wrapper}>
        {refineButton}
        {submitButton}
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      {refineButton}
      <div className={styles.actionButtonsContainer}>
        <TooltipContainer
          label={getScheduleTooltipLabel()}
          inPortal
          trigger={[TooltipTrigger.Hover]}
        >
          <Button
            className={styles.scheduleButton}
            type="button"
            data-testid="schedule-task-button"
            variant="tertiary"
            iconName="Clock"
            disabled={isScheduleButtonDisabled}
            onClick={handleScheduleClick}
          />
        </TooltipContainer>
        {submitButton}
      </div>
    </div>
  );
};
